import React, { useState, useEffect, useRef } from "react";
import { useField } from "formik";
import Editor from "@monaco-editor/react";
import type { Monaco } from "@monaco-editor/react";
// @ts-ignore
import type { editor } from "monaco-editor";
import { Maximize2, Pencil, Check, X } from "lucide-react";
import Modal from "@/components/ui/Modal";
import { Box, Text, IconButton, Button, Flex } from "@radix-ui/themes";
import { useTheme } from "@/providers/ThemeProvider";

interface CodeFieldProps {
  name: string;
  label?: string;
  description?: string | React.ReactNode;
  language: string;
  required?: boolean;
  isEditing?: boolean; // Parent component edit mode
  individualEditAllowed?: boolean; // Whether individual field editing is allowed
  typeDefinitions?: string; // TypeScript type definitions for IntelliSense
  disableValidation?: boolean; // Disable language validation (useful for templated JSON)
  onChange?: (value: string) => void;
  onEditSaved?: (name: string, value: string) => void;
  onIndividualEditModeChanged?: (isIndividualEditing: boolean) => void;
}

const CodeField = ({
  name,
  label,
  description,
  language,
  required,
  isEditing = true,
  individualEditAllowed = false,
  typeDefinitions,
  disableValidation = false,
  onChange,
  onEditSaved,
  onIndividualEditModeChanged,
}: CodeFieldProps) => {
  const { isDarkMode } = useTheme();
  const [field, meta, helpers] = useField(name);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isIndividualEditing, setIsIndividualEditing] = useState(false);
  const [tempValue, setTempValue] = useState<string>("");
  const typeDisposablesRef = useRef<Array<{ dispose: () => void }>>([]);

  const getValue = () => {
    if (typeof field.value === "string") {
      return field.value;
    }
    return JSON.stringify(field.value, null, 2);
  };

  useEffect(() => {
    const value =
      typeof field.value === "string"
        ? field.value
        : JSON.stringify(field.value, null, 2);
    setTempValue(value);
  }, [field.value]);

  // Reset individual editing state when parent edit mode changes
  useEffect(() => {
    if (isEditing) {
      setIsIndividualEditing(false);
    }
  }, [isEditing]);

  // Register type definitions with Monaco Editor
  const handleEditorMount = (
    _editor: editor.IStandaloneCodeEditor,
    monaco: Monaco,
  ) => {
    if (
      typeDefinitions &&
      (language === "javascript" || language === "typescript")
    ) {
      // Use JavaScript defaults for JavaScript files, TypeScript defaults for TypeScript files
      const languageDefaults =
        language === "javascript"
          ? monaco.languages.typescript.javascriptDefaults
          : monaco.languages.typescript.typescriptDefaults;

      // Add type definitions
      const disposable = languageDefaults.addExtraLib(
        typeDefinitions,
        `file:///types/${name}.d.ts`,
      );
      typeDisposablesRef.current.push(disposable);

      // Enable IntelliSense features
      languageDefaults.setCompilerOptions({
        target: monaco.languages.typescript.ScriptTarget.Latest,
        allowNonTsExtensions: true,
        checkJs: language === "javascript",
      });

      // Enable suggestions
      languageDefaults.setDiagnosticsOptions({
        noSemanticValidation: false,
        noSyntaxValidation: false,
        diagnosticCodesToIgnore: [1108],
      });
    }

    // Disable JSON validation if requested (useful for templated JSON with {{}} syntax)
    if (
      disableValidation &&
      (language === "json" || language === "application/json")
    ) {
      monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
        validate: false,
        allowComments: true,
        schemas: [],
        enableSchemaRequest: false,
      });
    }
  };

  // Filter validation markers when validation is disabled
  const handleValidate = (markers: editor.IMarker[]) => {
    if (disableValidation) {
      // Return empty array to suppress all validation errors
      return [];
    }
    return markers;
  };

  // Cleanup type definitions on unmount
  useEffect(() => {
    return () => {
      typeDisposablesRef.current.forEach((disposable) => {
        disposable.dispose();
      });
      typeDisposablesRef.current = [];
    };
  }, []);

  const handleEdit = () => {
    setIsIndividualEditing(true);
    setTempValue(getValue());
    onIndividualEditModeChanged?.(true);
  };

  const handleSave = () => {
    helpers.setValue(tempValue);
    onChange?.(tempValue);
    onEditSaved?.(name, tempValue);
    setIsIndividualEditing(false);
    setIsFullScreen(false); // Close fullscreen modal
    onIndividualEditModeChanged?.(false);
  };

  const handleCancel = () => {
    setTempValue(getValue());
    setIsIndividualEditing(false);
    setIsFullScreen(false); // Close fullscreen modal
    onIndividualEditModeChanged?.(false);
  };

  const handleEditorChange = (value: string | undefined) => {
    if (isIndividualEditing || isFullScreen) {
      setTempValue(value || "");
    } else {
      helpers.setValue(value || "");
      onChange?.(value || "");
    }
  };

  // Determine current field state
  const shouldRenderTextOnly = !isEditing && !isIndividualEditing;
  const shouldShowEditIcon =
    !isEditing && individualEditAllowed && !isIndividualEditing;
  const currentValue = isIndividualEditing ? tempValue : getValue();

  const editorOptions: editor.IStandaloneEditorConstructionOptions = {
    minimap: { enabled: false },
    suggestOnTriggerCharacters: true,
    quickSuggestions: true,
    parameterHints: { enabled: true },
    scrollBeyondLastLine: false,
  };

  return (
    <Box mb="4" width="100%">
      <Flex justify="between" align="center" mb="1">
        <Box mb="2">
          {label && (
            <Text as="label" htmlFor={name} size="2" weight="medium" mb="1">
              {label} {required && <Text color="red">{" *"}</Text>}
            </Text>
          )}

          {description && (
            <Box style={{ display: "block", marginBottom: "8px" }}>
              {typeof description === "string" ? (
                <Text size="2" color="gray">
                  {description}
                </Text>
              ) : (
                description
              )}
            </Box>
          )}
        </Box>

        {!shouldRenderTextOnly && (
          <IconButton
            type="button"
            onClick={() => {
              setTempValue(currentValue);
              setIsFullScreen(true);
            }}
            variant="ghost"
            size="2"
            title="Full screen"
          >
            <Maximize2 />
          </IconButton>
        )}
      </Flex>

      {shouldRenderTextOnly && shouldShowEditIcon && (
        <Box style={{ position: "relative", width: "100%" }}>
          <IconButton
            size="1"
            variant="ghost"
            onClick={handleEdit}
            aria-label="Edit"
            style={{
              position: "absolute",
              top: "32px",
              right: "8px",
              backgroundColor: "var(--color-panel-solid)",
              borderRadius: "var(--radius-2)",
              opacity: 0.8,
            }}
          >
            <Pencil className="w-4 h-4" />
          </IconButton>
        </Box>
      )}

      {!isFullScreen && (
        <Box>
          <Box
            onKeyDown={(e) => {
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();
            }}
            style={{
              position: "relative",
              border: "1px solid var(--gray-6)",
              borderRadius: "var(--radius-3)",
              overflow: "hidden",
            }}
          >
            <Editor
              height="300px"
              defaultLanguage={language}
              language={language}
              onChange={handleEditorChange}
              value={isIndividualEditing ? tempValue : currentValue}
              theme={isDarkMode ? "vs-dark" : "light"}
              onMount={handleEditorMount}
              onValidate={handleValidate}
              options={{
                readOnly: shouldRenderTextOnly,
                ...editorOptions,
              }}
            />
          </Box>

          {isIndividualEditing && !isEditing && (
            <Flex gap="2" mt="2" justify="start">
              <Button
                size="1"
                variant="soft"
                color="red"
                onClick={handleCancel}
              >
                <X size={12} style={{ marginRight: "4px" }} />
                Cancel
              </Button>
              <Button
                size="1"
                variant="soft"
                color="green"
                onClick={handleSave}
              >
                <Check size={12} style={{ marginRight: "4px" }} />
                Save
              </Button>
            </Flex>
          )}
        </Box>
      )}

      {meta.touched && meta.error && (
        <Text
          size="2"
          color="red"
          style={{ display: "block", marginTop: "4px" }}
        >
          {meta.error}
        </Text>
      )}

      <Modal
        isOpen={isFullScreen}
        onClose={() => setIsFullScreen(false)}
        title={label}
        size="full"
        className="h-[90vh]"
        actions={
          <Flex gap="2">
            <Button size="2" variant="soft" color="red" onClick={handleCancel}>
              <X size={16} style={{ marginRight: "4px" }} />
              Cancel
            </Button>
            <Button size="2" variant="soft" color="green" onClick={handleSave}>
              <Check size={16} style={{ marginRight: "4px" }} />
              Save
            </Button>
          </Flex>
        }
      >
        <Box
          height="100%"
          onKeyDown={(e) => {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
          }}
        >
          <Editor
            height="calc(90vh - 80px)"
            defaultLanguage={language}
            language={language}
            onChange={handleEditorChange}
            value={tempValue}
            theme={isDarkMode ? "vs-dark" : "light"}
            onMount={handleEditorMount}
            onValidate={handleValidate}
            options={{
              ...editorOptions,
            }}
          />
        </Box>
      </Modal>
    </Box>
  );
};

export default CodeField;
